// frontend/src/components/UI/SimpleDocumentViewerModal.js
/**
 * Lightweight document viewer modal — shows the raw file (PDF or image) inline.
 * Intentionally separate from DocumentVerificationModal (OCR editing tool).
 *
 * Fetches via api.viewDocumentBlob so JWT auth is applied (raw /media URLs
 * would bypass the Authorization header and 401).
 *
 * Props:
 *   isOpen       - boolean
 *   onClose      - dismiss callback
 *   filePath     - storage-relative path (e.g. W0227/professional_certificates/x.pdf)
 *   title        - header title
 *   fileName     - optional original filename used for type detection/download
 *   statusBadge  - optional ReactNode rendered next to the title
 *   onDownload   - download button handler (parent owns the download API call)
 *   downloading  - boolean, spinner on the download button
 *   theme        - 'admin' | 'employee' (default 'employee')
 */

import React, { useEffect, useState } from 'react';
import {
  Download,
  FileText,
  Maximize2,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import Modal from './Modal';
import PdfViewer from './PdfViewer';
import api from '../../services/api';
import { useImageViewer } from '../../hooks/useImageViewer';
import { getPreviewDocumentType } from '../../utils/imageUtils';

const SimpleDocumentViewerModal = ({
  isOpen,
  onClose,
  filePath,
  title,
  fileName,
  statusBadge,
  onDownload,
  downloading = false,
  theme = 'employee',
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingBlob, setLoadingBlob] = useState(false);
  const [blobError, setBlobError] = useState(false);
  const viewer = useImageViewer();

  const isAdmin = theme === 'admin';
  const iconButtonClass = isAdmin ? 'icon-button-admin' : 'icon-button';
  const closeClass = isAdmin ? 'modal-close-admin' : 'modal-close';
  const spinnerBorderClass = isAdmin
    ? 'border-red-800 border-t-transparent'
    : 'border-blue-800 border-t-transparent';
  const loadingSpinnerClass = isAdmin
    ? 'border-red-200 border-t-red-600'
    : 'border-blue-200 border-t-blue-600';
  const viewerButtonClass = `p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 ${
    isAdmin ? 'focus-visible:ring-red-500' : 'focus-visible:ring-blue-500'
  }`;

  useEffect(() => {
    if (!isOpen || !filePath) {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setBlobError(false);
      return undefined;
    }

    let revoked = false;
    setLoadingBlob(true);
    setBlobError(false);
    setBlobUrl(null);

    api.viewDocumentBlob(filePath)
      .then((res) => {
        if (revoked) return;
        const url = URL.createObjectURL(
          new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' })
        );
        setBlobUrl(url);
      })
      .catch(() => {
        if (!revoked) setBlobError(true);
      })
      .finally(() => {
        if (!revoked) setLoadingBlob(false);
      });

    return () => {
      revoked = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filePath]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobUrl]);

  useEffect(() => {
    viewer.reset();
  }, [filePath, isOpen, viewer.reset]);

  if (!isOpen || !filePath) return null;

  const previewType = getPreviewDocumentType(fileName || filePath);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      theme={theme}
      contentClassName="!overflow-hidden !p-0 min-h-[60vh] flex items-center justify-center bg-gray-50"
      customHeader={(
        <div className="modal-header">
          {/* DS modal-header: title + status on the left; actions alone on the right.
              No decorative FileText icon — the title already names the document. */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <h3 className="modal-title truncate min-w-0">{title}</h3>
              {statusBadge ? <div className="flex-shrink-0">{statusBadge}</div> : null}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-3 shrink-0">
            {onDownload && (
              <button
                type="button"
                onClick={onDownload}
                disabled={downloading}
                className={iconButtonClass}
                title="Download"
                aria-label="Download document"
              >
                {downloading
                  ? <div className={`w-4 h-4 border-2 ${spinnerBorderClass} rounded-full animate-spin`} />
                  : <Download className="w-4 h-4" />}
              </button>
            )}
            <button type="button" onClick={onClose} className={closeClass} title="Close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    >
      {loadingBlob && (
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className={`w-8 h-8 border-2 ${loadingSpinnerClass} rounded-full animate-spin`} />
          <span className="text-sm">Loading document…</span>
        </div>
      )}
      {blobError && !loadingBlob && (
        <div className="flex flex-col items-center gap-2 text-gray-400 px-6 text-center">
          <FileText className="w-10 h-10" />
          <span className="text-sm">Unable to load document. Please try downloading it instead.</span>
        </div>
      )}
      {blobUrl && !loadingBlob && (
        previewType === 'pdf' ? (
          <div className="w-full h-[70vh]">
            <PdfViewer
              src={blobUrl}
              title={title}
              fileName={fileName || 'document.pdf'}
              theme={theme}
              onDownload={onDownload}
            />
          </div>
        ) : previewType === 'image' ? (
          <div
            ref={viewer.containerRef}
            {...viewer.panHandlers}
            style={viewer.containerStyle}
            className="relative flex items-center justify-center w-full h-[70vh] overflow-hidden bg-gray-50"
          >
            <img
              src={blobUrl}
              alt={title || 'Document preview'}
              className="max-w-full max-h-full object-contain select-none"
              draggable="false"
              style={{
                transform: viewer.imageTransform,
                transformOrigin: 'center center',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden flex-col items-center text-gray-400 gap-2">
              <FileText className="w-10 h-10" />
              <span className="text-sm">Unable to preview this file type</span>
            </div>
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-0.5 bg-white/95 backdrop-blur border border-gray-200 rounded-full shadow-md p-1"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button type="button" onClick={viewer.zoomOut} className={viewerButtonClass} title="Zoom out" aria-label="Zoom out">
                <ZoomOut className="w-4 h-4" />
              </button>
              <button type="button" onClick={viewer.zoomIn} className={viewerButtonClass} title="Zoom in" aria-label="Zoom in">
                <ZoomIn className="w-4 h-4" />
              </button>
              <button type="button" onClick={viewer.rotate} className={viewerButtonClass} title="Rotate" aria-label="Rotate document">
                <RotateCw className="w-4 h-4" />
              </button>
              <button type="button" onClick={viewer.reset} className={viewerButtonClass} title="Fit to panel" aria-label="Fit document to panel">
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 px-6 text-center text-gray-400">
            <FileText className="w-10 h-10" />
            <span className="text-sm">Preview is unavailable for this file type. Download the original file instead.</span>
          </div>
        )
      )}
    </Modal>
  );
};

export default SimpleDocumentViewerModal;
